<script setup lang="ts">
/** Sidebar footer: the measured state of the platform's links, every 30 s. */
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/api'
import Dot from '@/ui/Dot.vue'

defineProps<{ collapsed: boolean }>()
const { t } = useI18n()
const q = useQuery({
  queryKey: ['system', 'health'],
  queryFn: () => api<{ resume: { ok: number; degraded: number; down: number; unknown: number } }>('/system/health'),
  refetchInterval: 30_000,
})
const state = computed(() => {
  if (q.isPending.value) return { tone: 'neutral' as const, text: t('shell.health.probing') }
  if (q.isError.value || !q.data.value) return { tone: 'warning' as const, text: t('shell.health.unknown') }
  const r = q.data.value.resume
  if (r.down) return { tone: 'danger' as const, text: t('shell.health.down', { count: r.down }, r.down) }
  if (r.degraded) return { tone: 'warning' as const, text: t('shell.health.degraded', { count: r.degraded }, r.degraded) }
  if (r.unknown) return { tone: 'neutral' as const, text: t('shell.health.unmeasured', { count: r.unknown }, r.unknown) }
  return { tone: 'success' as const, text: t('shell.health.ok') }
})
</script>

<template>
  <RouterLink to="/system" :title="collapsed ? state.text : undefined"
              class="flex items-center gap-2 rounded-input px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-muted">
    <Dot :tone="state.tone" :live="state.tone === 'danger'" />
    <span v-if="!collapsed" class="min-w-0 truncate" role="status">{{ state.text }}</span>
  </RouterLink>
</template>
